/*
  RUMI scratch-off promo modal — behaviour.
  Section: sections/scratch-modal.liquid (Figma 461:37683 / 461:34129).

  Copy and theme settings stay in Liquid: everything here is read off data-*
  attributes on the root. There are always two routes to the prize — the
  pointer-driven scratch canvas, which reveals past a coverage threshold, and a
  real focusable "Reveal" button, which is the only route under
  prefers-reduced-motion or without canvas / Pointer Events and is available to
  keyboard and screen reader users in every mode.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'rumi.scratch.v1';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var FOIL_STOPS = [
    [0, '#cba24a'], [0.18, '#e6c87d'], [0.35, '#b8913c'], [0.52, '#ddbc6a'],
    [0.7, '#a8842f'], [0.85, '#d9b862'], [1, '#b99a45']
  ];

  function suppress(days, won, once) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          until: Date.now() + days * 86400000,
          won: !!won,
          once: !!once
        })
      );
    } catch (err) {
      /* Private browsing: the cap is lost, the modal still works. */
    }
  }

  function suppressed() {
    var state;
    try {
      state = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (err) {
      return false;
    }
    if (state.once) return true;
    return typeof state.until === 'number' && Date.now() < state.until;
  }

  function canScratch() {
    if (!('PointerEvent' in window)) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
    var probe = document.createElement('canvas');
    return !!(probe.getContext && probe.getContext('2d'));
  }

  function ScratchModal(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-scratch-dialog]');
    this.card = root.querySelector('[data-scratch-card]');
    this.canvas = root.querySelector('[data-scratch-canvas]');
    this.revealBtn = root.querySelector('[data-scratch-reveal]');
    this.claim = root.querySelector('[data-scratch-claim]');
    this.copyBtn = root.querySelector('[data-scratch-copy]');
    this.codeEl = root.querySelector('[data-scratch-code]');
    this.status = root.querySelector('[data-scratch-status]');
    this.announce = root.querySelector('[data-scratch-announce]');

    this.frequency = parseFloat(root.getAttribute('data-frequency')) || 14;
    this.trigger = root.getAttribute('data-trigger') || 'exit';
    this.once = root.getAttribute('data-once') !== '0';
    this.delay = parseFloat(root.getAttribute('data-delay'));
    this.threshold = (parseFloat(root.getAttribute('data-threshold')) || 50) / 100;
    this.label = root.getAttribute('data-label') || '';
    this.copyDone = root.getAttribute('data-copy-done') || 'Code copied';
    this.copyFail = root.getAttribute('data-copy-fail') || 'Select the code and copy it';
    this.wonMessage = root.getAttribute('data-won-message') || '';

    this.open = false;
    this.revealed = false;
    this.painted = false;
    this.drawing = false;
    this.lastPoint = null;
    this.lastMeasure = 0;
    this.measureQueued = false;
    this.returnFocus = null;

    this.mode = canScratch() && this.canvas ? 'scratch' : 'button';
    root.setAttribute('data-mode', this.mode);

    this.bind();
  }

  ScratchModal.prototype.bind = function () {
    var self = this;

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-scratch-close]'), function (el) {
      el.addEventListener('click', function () {
        self.hide(true);
      });
    });

    if (this.revealBtn) {
      this.revealBtn.addEventListener('click', function () {
        self.reveal(true);
      });
    }

    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', function () {
        self.copyCode();
      });
    }

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        self.hide(true);
      } else if (event.key === 'Tab') {
        self.trapFocus(event);
      }
    });

    if (this.mode === 'scratch') this.bindPointer();

    /* Repaint only when the card actually changed size — a bare resize event
       (a rotating phone fires several) must not wipe someone's progress. */
    window.addEventListener('resize', function () {
      if (!self.open || self.revealed || self.mode !== 'scratch') return;
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () {
        var width = self.card.getBoundingClientRect().width;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        if (Math.round(width * dpr) !== self.canvas.width) self.paint();
      }, 200);
    });
  };

  /* --- Open / close -------------------------------------------------- */

  ScratchModal.prototype.show = function () {
    if (this.open) return;
    this.open = true;
    /* "Only once" means once SEEN, not once dismissed — otherwise closing the
       tab without touching it would bring the card back on the next visit. */
    if (this.once) suppress(this.frequency, this.revealed, true);
    this.returnFocus = document.activeElement;

    this.root.hidden = false;
    document.documentElement.classList.add('rumi-scratch-lock');

    var self = this;
    window.requestAnimationFrame(function () {
      self.root.classList.add('is-open');
      if (self.mode === 'scratch') self.paint();
      var first = self.root.querySelector('[data-scratch-close]');
      if (first) first.focus();
    });
  };

  ScratchModal.prototype.hide = function (remember) {
    if (!this.open) return;
    this.open = false;
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('rumi-scratch-lock');

    if (remember) suppress(this.frequency, this.revealed, this.once);

    var self = this;
    window.setTimeout(function () {
      self.root.hidden = true;
    }, 300);

    if (this.returnFocus && typeof this.returnFocus.focus === 'function') {
      this.returnFocus.focus();
    }
    this.returnFocus = null;
  };

  ScratchModal.prototype.trapFocus = function (event) {
    /* getClientRects rather than offsetParent: the close button is fixed, and
       fixed elements report a null offsetParent even when plainly visible. */
    var items = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.getClientRects().length > 0;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* --- Foil ---------------------------------------------------------- */

  ScratchModal.prototype.paint = function () {
    if (!this.canvas || this.revealed) return;
    var rect = this.card.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(rect.width * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.dpr = dpr;

    var ctx = this.canvas.getContext('2d');
    if (!ctx) {
      this.root.setAttribute('data-mode', 'button');
      this.mode = 'button';
      return;
    }
    this.ctx = ctx;

    var w = this.canvas.width;
    var h = this.canvas.height;

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);

    var foil = ctx.createLinearGradient(0, 0, w, h);
    FOIL_STOPS.forEach(function (stop) {
      foil.addColorStop(stop[0], stop[1]);
    });
    ctx.fillStyle = foil;
    ctx.fillRect(0, 0, w, h);

    var sheen = ctx.createRadialGradient(w * 0.32, h * 0.26, 0, w * 0.32, h * 0.26, Math.max(w, h) * 0.8);
    sheen.addColorStop(0, 'rgba(255, 246, 214, 0.45)');
    sheen.addColorStop(1, 'rgba(255, 246, 214, 0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, w, h);

    /* Deterministic brushed-metal streaks — no Math.random, so a repaint after
       a resize looks like the same piece of foil. */
    ctx.save();
    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = '#7a5d16';
    ctx.lineWidth = Math.max(1, w / 320);
    for (var i = 0; i < 80; i++) {
      var x = (((i * 97) % 100) / 100) * w * 1.4 - w * 0.2;
      ctx.beginPath();
      ctx.moveTo(x, -h * 0.1);
      ctx.lineTo(x - w * 0.28, h * 1.1);
      ctx.stroke();
    }
    ctx.restore();

    if (this.label) {
      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if ('letterSpacing' in ctx) ctx.letterSpacing = Math.round(w * 0.002) + 'px';
      ctx.font =
        '523 ' + Math.round(w * 0.093) + "px 'Special Gothic', 'Helvetica Neue', Arial, sans-serif";
      ctx.fillText(this.label, w / 2, h / 2);
      ctx.restore();
    }

    this.painted = true;

    /* Repaint once the display face is live, so the label is not left stamped
       in the fallback stack. Skipped if the visitor has already started. */
    if (!this.fontsPending && document.fonts && document.fonts.ready) {
      this.fontsPending = true;
      var self = this;
      document.fonts.ready.then(function () {
        if (!self.scratched) self.paint();
      });
    }
  };

  /* --- Scratching ---------------------------------------------------- */

  ScratchModal.prototype.bindPointer = function () {
    var self = this;
    var canvas = this.canvas;

    canvas.addEventListener('pointerdown', function (event) {
      if (self.revealed || !self.painted) return;
      event.preventDefault();
      self.drawing = true;
      if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId);
      self.lastPoint = self.toCanvas(event);
      self.stroke(self.lastPoint, self.lastPoint);
    });

    canvas.addEventListener('pointermove', function (event) {
      if (!self.drawing || self.revealed) return;
      event.preventDefault();
      var point = self.toCanvas(event);
      self.stroke(self.lastPoint || point, point);
      self.lastPoint = point;
      self.scheduleMeasure();
    });

    /* No pointerleave: the canvas has pointer capture, so dragging off the card
       and back is one continuous stroke, and pointerup still lands here. */
    ['pointerup', 'pointercancel'].forEach(function (name) {
      canvas.addEventListener(name, function () {
        if (!self.drawing) return;
        self.drawing = false;
        self.lastPoint = null;
        self.measure();
      });
    });
  };

  ScratchModal.prototype.toCanvas = function (event) {
    var r = this.canvas.getBoundingClientRect();
    var c = this.canvas;
    return { x: ((event.clientX - r.left) / r.width) * c.width, y: ((event.clientY - r.top) / r.height) * c.height };
  };

  ScratchModal.prototype.stroke = function (from, to) {
    var ctx = this.ctx;
    if (!ctx) return;
    this.scratched = true;
    var brush = this.canvas.width * 0.14;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brush;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    /* A tap is a zero-length line, which strokes nothing in some engines. */
    ctx.beginPath();
    ctx.arc(to.x, to.y, brush / 2, 0, Math.PI * 2);
    ctx.fill();
  };

  /* Coverage is sampled on a coarse grid rather than pixel by pixel, and at
     most a few times a second, so dragging stays cheap on a phone. */
  ScratchModal.prototype.scheduleMeasure = function () {
    var self = this;
    if (this.measureQueued) return;
    if (Date.now() - this.lastMeasure < 140) return;
    this.measureQueued = true;
    window.requestAnimationFrame(function () {
      self.measureQueued = false;
      self.measure();
    });
  };

  ScratchModal.prototype.measure = function () {
    if (this.revealed || !this.ctx) return;
    this.lastMeasure = Date.now();

    var w = this.canvas.width;
    var h = this.canvas.height;
    var step = Math.max(4, Math.round(8 * (this.dpr || 1)));
    var data;

    try {
      data = this.ctx.getImageData(0, 0, w, h).data;
    } catch (err) {
      /* Nothing taints this canvas today, but never leave a player stuck. */
      this.reveal(false);
      return;
    }

    var total = 0;
    var cleared = 0;
    for (var y = 0; y < h; y += step) {
      var row = y * w * 4;
      for (var x = 0; x < w; x += step) {
        total++;
        if (data[row + x * 4 + 3] < 128) cleared++;
      }
    }

    if (total && cleared / total >= this.threshold) this.reveal(false);
  };

  /* --- Reveal and claim ---------------------------------------------- */

  ScratchModal.prototype.reveal = function (fromButton) {
    if (this.revealed) return;
    this.revealed = true;
    this.drawing = false;

    this.root.classList.add('is-revealed');
    if (this.card) this.card.setAttribute('aria-hidden', 'false');
    if (this.claim) this.claim.hidden = false;
    if (this.revealBtn) this.revealBtn.hidden = true;
    if (this.announce) this.announce.textContent = this.wonMessage;

    /* A win is a win: do not ask again inside the window. */
    suppress(this.frequency, true, this.once);

    /* Only take focus when the reveal came from the keyboard route — stealing
       it mid-gesture would fight a pointer user. */
    if (fromButton && this.copyBtn) this.copyBtn.focus();
  };

  ScratchModal.prototype.copyCode = function () {
    var self = this;
    var code = this.codeEl ? this.codeEl.textContent.trim() : '';
    if (!code) return;

    var done = function () {
      self.setStatus(self.copyDone);
    };
    var failed = function () {
      if (self.legacyCopy(code)) done();
      else {
        self.setStatus(self.copyFail);
        self.selectCode();
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, failed);
    } else {
      failed();
    }
  };

  ScratchModal.prototype.legacyCopy = function (code) {
    var field = document.createElement('textarea');
    field.value = code;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
    document.body.appendChild(field);
    var ok = false;
    try {
      field.select();
      ok = document.execCommand('copy');
    } catch (err) {
      ok = false;
    }
    document.body.removeChild(field);
    return ok;
  };

  ScratchModal.prototype.selectCode = function () {
    if (!this.codeEl || !window.getSelection || !document.createRange) return;
    var range = document.createRange();
    range.selectNodeContents(this.codeEl);
    var selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  /* Written to a role="status" node, so it is both seen and announced. */
  ScratchModal.prototype.setStatus = function (message) {
    if (this.status) this.status.textContent = message;
  };

  /* --- Boot ---------------------------------------------------------- */

  function start(root) {
    if (!root || root.dataset.scratchReady === '1') return;
    root.dataset.scratchReady = '1';

    var modal = new ScratchModal(root);
    root.rumiScratch = modal;

    var designMode = !!(window.Shopify && window.Shopify.designMode);

    if (designMode) {
      var section = root.closest('.shopify-section');
      document.addEventListener('shopify:section:select', function (event) {
        if (!section || event.target === section) modal.show();
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (!section || event.target === section) modal.hide(false);
      });
      return;
    }

    if (suppressed()) return;

    var delay = isNaN(modal.delay) ? 8 : modal.delay;

    function showOnce() {
      if (modal.open || suppressed()) return;
      modal.show();
    }

    /* The timer alone is not enough to open the card: the visitor also has
       to have touched the page — a scroll, a tap, a key. Two reasons. A
       promo that covers the screen before anyone has engaged is the kind of
       interstitial Google penalises on mobile. And Lighthouse never
       interacts, so a modal on a bare timer was being measured as the
       page's largest contentful paint at fifteen seconds, dragging the
       whole performance score down for a thing no real visitor sees that
       way. Whichever of the two comes second opens the card. */
    var INTERACTION_EVENTS = ['scroll', 'pointerdown', 'touchstart', 'keydown'];

    function armDelay() {
      var elapsed = false;
      var interacted = false;

      function maybeShow() {
        if (elapsed && interacted) showOnce();
      }

      function onInteract(event) {
        /* Capture-phase listening also hears scroll events from elements —
           the announcement bar's auto-rotating slider fires one every few
           seconds with nobody touching anything. Only the page scrolling
           counts. */
        if (event.type === 'scroll' && event.target !== document && event.target !== window) return;
        interacted = true;
        INTERACTION_EVENTS.forEach(function (name) {
          window.removeEventListener(name, onInteract, true);
        });
        maybeShow();
      }

      INTERACTION_EVENTS.forEach(function (name) {
        window.addEventListener(name, onInteract, { capture: true, passive: true });
      });

      window.setTimeout(function () {
        elapsed = true;
        maybeShow();
      }, Math.max(0, delay) * 1000);
    }

    if (modal.trigger !== 'exit') {
      armDelay();
      return;
    }

    /* Exit intent. The cursor leaving through the TOP of the window is the
       only signal that reliably means "heading for the address bar or a tab"
       rather than just crossing a window edge — leaving left, right or bottom
       is ordinary mouse movement. relatedTarget/toElement being empty is what
       distinguishes leaving the window from moving between elements.

       Touch devices have no cursor and therefore no equivalent signal; the
       usual substitutes (fast scroll up, history-back traps) misfire on
       ordinary browsing, so those fall back to the timed trigger instead. The
       schema says so. */
    var pointerIsFine = !window.matchMedia || window.matchMedia('(pointer: fine)').matches;
    if (!pointerIsFine) {
      armDelay();
      return;
    }

    /* A grace period stops the card firing at someone whose cursor happens to
       be near the top of the window as the page loads. */
    var armedAt = Date.now() + 3000;

    function onMouseOut(event) {
      if (Date.now() < armedAt) return;
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > 0) return;
      document.removeEventListener('mouseout', onMouseOut);
      showOnce();
    }

    document.addEventListener('mouseout', onMouseOut);
  }

  function boot() {
    start(document.querySelector('[data-rumi-scratch]'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    boot();
  });
})();
